import { useState, useEffect } from 'react';
import { GRADE_COLOR, HelpTip, Loading, PANEL_HELP, PanelHowTo, api } from './_barrel.js';


        function BusinessOwnershipControl({ token, businessId, hasCompany }) {
            const [pscs, setPscs] = useState(null);
            const [charges, setCharges] = useState(null);
            const [pscStatus, setPscStatus] = useState(null);
            const [loading, setLoading] = useState(false);
            const [err, setErr] = useState(null);

            const load = async () => {
                setLoading(true); setErr(null);
                try {
                    const [p, c, st] = await Promise.all([
                        api(`/api/v1/businesses/${businessId}/ch-pscs`, {}, token).catch(() => []),
                        api(`/api/v1/businesses/${businessId}/ch-charges`, {}, token).catch(() => null),
                        api(`/api/v1/businesses/${businessId}/ch-psc-status`, {}, token).catch(() => null),
                    ]);
                    setPscs(p); setCharges(c); setPscStatus(st);
                } catch (e) { setErr(e.message); } finally { setLoading(false); }
            };

            // Auto-load on first open: an analyst viewing a business for the first
            // time should see who controls it without having to know there's a
            // hidden "Load" button — this is also what populates business_pscs,
            // which both the UBO panel and the Entity graph read from.
            useEffect(() => { if (hasCompany) load(); }, [businessId, hasCompany]);

            if (!hasCompany) return null;

            return (
              <div className="panel mb-6">
                <div className="panel-header flex justify-between items-center">
                  <div>
                    <div className="font-display text-xl gold">Ownership &amp; Charges<HelpTip text={PANEL_HELP.ownership_charges} /></div>
                    <div className="text-xs text-muted font-mono mt-1">Who controls it · secured debt · Companies House</div>
                  </div>
                  <button className="btn-ghost" onClick={load} disabled={loading}>{loading ? 'Loading…' : (pscs === null ? 'Load' : 'Reload')}</button>
                </div>
                <PanelHowTo id="ownership_charges" />
                <div className="px-6 py-5">
                  {err && <div className="text-[var(--red)] text-sm mb-3">{err}</div>}
                  {pscs === null ? (
                    <div className="text-sm text-muted">{loading ? 'Loading…' : 'Click Load to pull persons with significant control and registered charges from Companies House.'}</div>
                  ) : (
                    <>
                      <div className="text-xs uppercase tracking-widest text-muted font-mono mb-2" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span>Persons with Significant Control ({pscs.length})</span>
                        {pscs.length > 0 && (
                          <span title={`Source: ${pscs[0].source} · reliability ${pscs[0].reliability} (${pscs[0].reliability_label}). Grade A = official statutory register.`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', textTransform: 'none', letterSpacing: 'normal' }}>
                            <span style={{ width: '13px', height: '13px', borderRadius: '3px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '9px', color: '#0b0b0d', background: (GRADE_COLOR[pscs[0].reliability] || 'var(--text-muted)') }}>{pscs[0].reliability}</span>
                            <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{pscs[0].source}</span>
                          </span>
                        )}
                      </div>
                      {pscStatus && pscStatus.state !== 'has_psc' && pscStatus.text && (
                        <div className="text-sm mb-3" style={pscStatus.state === 'no_psc' ? { color: 'var(--amber)' } : { color: 'var(--text-soft)' }}>
                          {pscStatus.state === 'exempt' && <span className="font-mono text-xs mr-2" style={{ color: 'var(--gold)' }}>EXEMPT</span>}
                          {pscStatus.text}
                        </div>
                      )}
                      {pscs.length === 0 ? (pscStatus ? <div className="mb-5" /> : <div className="text-sm text-muted mb-5">No PSC on the register — worth a manual ownership check.</div>) : (
                        <div className="mb-5">
                          {pscs.map((p, i) => (
                            <div key={i} className="table-row px-4 py-3" style={p.flag ? { borderLeft: '3px solid var(--amber)' } : {}}>
                              <div className="flex justify-between items-start gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm">{p.name} {p.is_ceased && <span className="text-xs text-muted">· ceased</span>}</div>
                                  <div className="text-xs text-muted font-mono mt-1">
                                    {(p.nature_of_control || []).join(' · ') || p.statement || '—'}
                                    {p.country_of_residence && ` · ${p.country_of_residence}`}
                                    {p.identification_country && ` · reg. ${p.identification_country}`}
                                  </div>
                                </div>
                                {p.flag && <span className="font-mono text-xs" style={{ color: 'var(--amber)' }}>⚑ FLAG</span>}
                              </div>
                              {p.flag && <div className="text-xs mt-1" style={{ color: 'var(--amber)' }}>{p.flag}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {charges && (
                        <>
                          <div className="text-xs uppercase tracking-widest text-muted font-mono mb-2">Charges</div>
                          <div className="text-sm mb-2" style={charges.outstanding > 0 ? { color: 'var(--amber)' } : {}}>{charges.summary}</div>
                          {(charges.charges || []).slice(0, 8).map((c, i) => (
                            <div key={i} className="text-xs font-mono text-muted py-1">
                              {c.status && `[${c.status}] `}{c.description || c.classification || 'charge'}
                              {c.created_on && ` · ${c.created_on}`}
                              {(c.persons_entitled || []).length > 0 && ` · ${c.persons_entitled.slice(0, 2).join(', ')}`}
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
        }

export { BusinessOwnershipControl };
